import { useCallback, useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as Combobox from '@givebutter/primitives/combobox';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { useToast } from '@givebutter/primitives/toast';
import { fetchFunds } from 'api/funds';
import {
    QuickBooksRuleRequest,
    createQuickBooksRule,
    fetchQuickBooksRuleById,
    updateQuickBooksRule,
} from 'api/quickBooks';
import { useQuickBooksDetails } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/useQuickBooksDetails';
import { useQuickBooksRefreshMapping } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/useQuickBooksRefreshMapping';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { FUNDS_PARAMS } from 'constants/params';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteCampaigns } from 'customHooks/useInfiniteCampaigns';
import { useIsPending } from 'customHooks/useIsPending';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { InvalidReason, TransactionMethod } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface QuickBooksRuleFormValues {
    name: string;
    fundId: string[];
    campaignId: string[];
    paymentMethod: TransactionMethod[];
    mappingServiceItem: string[];
    mappingClass: string[];
    mappingAccount: string[];
    doNotSync: boolean;
    selectAtleastOneCriterion: boolean;
}

const QuickBooksRuleDrawer = () => {
    const { ruleId } = useParams<{ ruleId: string }>();
    const isEditing = !!ruleId;
    const ruleIdInt = parseInt(ruleId);
    const { accountId } = useCurrentAccount();
    const { error, success } = useToast();
    const { goBack } = useDialogRoute();
    const [shouldExitAfterCreate, setShouldExitAfterCreate] = useState(true);
    const { data: qbDetails } = useQuickBooksDetails();
    const { mapping } = qbDetails;
    const { isPending: isRefreshingMapping, mutate: refreshMapping } =
        useQuickBooksRefreshMapping();

    const { data: rule } = useSuspenseQuery({
        queryKey: accountQueryKeys.quickBooksRuleById(accountId, ruleIdInt),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchQuickBooksRuleById(accountId, ruleIdInt),
                isEditing,
            ),
    });

    const { data: fundsData } = useSuspenseQuery({
        queryKey: accountQueryKeys.fundsWithFilters(accountId, FUNDS_PARAMS),
        queryFn: () => fetchFunds(accountId, FUNDS_PARAMS),
    });
    const funds = fundsData.data;

    const [searchCampaigns, setSearchCampaigns] = useState(rule?.campaign_id?.name ?? '');
    const debouncedSearchCampaigns = useDebounce(searchCampaigns);
    const [isPendingCampaigns, params] = useIsPending(
        {
            query: debouncedSearchCampaigns || undefined,
            limit: 99,
        },
        500,
    );
    const { data: campaignsData, ref: campaignsRef } = useInfiniteCampaigns(params);
    const campaigns = campaignsData?.pages ?? [];

    const methods = Form.useGivebutterForm<QuickBooksRuleFormValues>({
        defaultValues: {
            name: rule?.name ?? '',
            fundId:
                rule?.fund_id && rule?.invalid_reason !== 'fund_not_found'
                    ? [rule.fund_id.id.toString()]
                    : [],
            campaignId:
                rule?.campaign_id && rule?.invalid_reason !== 'campaign_not_found'
                    ? [rule.campaign_id.id.toString()]
                    : [],
            mappingServiceItem:
                rule?.mapping.service_item?.id && rule?.invalid_reason !== 'service_item_not_found'
                    ? [rule?.mapping.service_item?.id]
                    : [],
            mappingClass:
                rule?.mapping.class?.id && rule?.invalid_reason !== 'class_not_found'
                    ? [rule?.mapping.class?.id]
                    : [],
            mappingAccount:
                rule?.mapping.account?.id && rule?.invalid_reason !== 'account_not_found'
                    ? [rule?.mapping.account?.id]
                    : [],
            paymentMethod: rule?.payment_method ? [rule.payment_method] : [],
            doNotSync: rule?.do_not_sync ?? false,
            selectAtleastOneCriterion: false,
        },
    });

    const { control, handleSubmit, path, watch, reset, setError } = methods;
    const [doNotSync, fundId, campaignId, paymentMethod] = watch([
        'doNotSync',
        'fundId',
        'campaignId',
        'paymentMethod',
    ]);

    const { mutate: create, isPending: createIsPending } = useMutation({
        mutationFn: (values: QuickBooksRuleFormValues) =>
            createQuickBooksRule(accountId, transformRuleFormIntoRequest(values)),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.quickBooksRules(accountId),
            });
            success('New rule created!');
            if (shouldExitAfterCreate) {
                goBack();
            } else {
                reset();
            }
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unknown error');
        },
    });

    const { mutate: update, isPending: updateIsPending } = useMutation({
        mutationFn: (values: QuickBooksRuleFormValues) =>
            updateQuickBooksRule(accountId, ruleIdInt, transformRuleFormIntoRequest(values)),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.quickBooksRules(accountId),
            });
            success('Rule updated!');
            goBack();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unknown error');
        },
    });

    const isPending = createIsPending ?? updateIsPending;

    useEffect(() => {
        if (!rule?.invalid) {
            return;
        }
        const error = getInvalidRuleError(rule?.invalid_reason);

        if (!error) {
            return;
        }

        const { key, message } = error;
        setError(key, { message });
    }, [rule?.invalid, rule?.invalid_reason, setError]);

    const validateCriterion = useCallback(() => {
        return fundId.length > 0 || campaignId.length > 0 || paymentMethod.length > 0;
    }, [campaignId.length, fundId.length, paymentMethod.length]);

    return (
        <Form.GivebutterFormProvider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(
                    (isEditing ? update : create) as SubmitHandler<QuickBooksRuleFormValues>,
                )}
            >
                <Drawer.Header>
                    <Drawer.Title>{isEditing ? 'Edit' : 'Add new'} rule</Drawer.Title>
                </Drawer.Header>
                <Drawer.Body>
                    <div className="flex flex-col gap-4">
                        <Form.Container>
                            <Form.Label name={path('name')}>
                                Name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name={path('name')}
                                rules={{ required: 'Name is required' }}
                            >
                                <Form.TextInputForm placeholder="Custom Rule 1" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('name')} />
                        </Form.Container>

                        <Form.Container>
                            <p className="text-lg font-semibold">
                                When transaction criteria matches:
                            </p>
                            <Form.Checkbox
                                name={path('selectAtleastOneCriterion')}
                                rules={{
                                    validate: () =>
                                        validateCriterion() || 'Select at least one criterion',
                                }}
                                hidden={true}
                            />
                            <Form.Error name={path('selectAtleastOneCriterion')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label name={path('fundId')}>Fund</Form.Label>
                            <Form.Combobox
                                resource="fund"
                                control={control}
                                name={path('fundId')}
                                allowMultiple={false}
                                rules={{
                                    validate: () => validateCriterion() || '',
                                }}
                            >
                                <Combobox.Trigger aria-label="Select fund">
                                    <Combobox.Select placeholder="Select fund" />
                                </Combobox.Trigger>
                                <Combobox.Popover>
                                    <Combobox.Search />
                                    <Combobox.Items>
                                        {funds.map(fund => (
                                            <Combobox.Item key={fund.id} value={fund.id.toString()}>
                                                <Combobox.ItemText>{fund.name}</Combobox.ItemText>
                                                <Combobox.CheckmarkIndicator />
                                            </Combobox.Item>
                                        ))}
                                    </Combobox.Items>
                                </Combobox.Popover>
                            </Form.Combobox>
                            <Form.Error name={path('fundId')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label name={path('campaignId')}>Campaign</Form.Label>
                            <Form.Combobox
                                resource="campaign"
                                control={control}
                                name={path('campaignId')}
                                allowMultiple={false}
                                rules={{
                                    validate: () => validateCriterion() || '',
                                }}
                            >
                                <Combobox.Trigger aria-label="Select campaign">
                                    <Combobox.Select placeholder="Select campaign" />
                                </Combobox.Trigger>
                                <Combobox.Popover>
                                    <Combobox.Search
                                        value={searchCampaigns}
                                        onChangeValue={setSearchCampaigns}
                                    />
                                    <Combobox.Items>
                                        <LoadingOverlay isLoading={isPendingCampaigns}>
                                            {campaigns.map(campaign => (
                                                <Combobox.Item
                                                    key={campaign.id}
                                                    value={campaign.id.toString()}
                                                >
                                                    <Combobox.ItemText>
                                                        {campaign.title}
                                                    </Combobox.ItemText>
                                                    <Combobox.CheckmarkIndicator />
                                                </Combobox.Item>
                                            ))}
                                            <div ref={campaignsRef} />
                                        </LoadingOverlay>
                                    </Combobox.Items>
                                </Combobox.Popover>
                            </Form.Combobox>
                            <Form.Error name={path('campaignId')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label name={path('paymentMethod')}>Payment method</Form.Label>
                            <Form.Combobox
                                resource="payment method"
                                control={control}
                                name={path('paymentMethod')}
                                allowMultiple={false}
                                rules={{
                                    validate: () =>
                                        fundId.length > 0 ||
                                        campaignId.length > 0 ||
                                        paymentMethod.length > 0 ||
                                        '',
                                }}
                            >
                                <Combobox.Trigger aria-label="Select payment method">
                                    <Combobox.Select placeholder="Select payment method" />
                                </Combobox.Trigger>
                                <Combobox.Popover>
                                    <Combobox.Items>
                                        <Combobox.Item value="ach">
                                            <Combobox.ItemText>ACH</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="card">
                                            <Combobox.ItemText>Credit Card</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="cash">
                                            <Combobox.ItemText>Cash</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="check">
                                            <Combobox.ItemText>Check</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="digital_wallet">
                                            <Combobox.ItemText>Digital Wallet</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="donor_advised_fund">
                                            <Combobox.ItemText>
                                                Donor Advised Fund
                                            </Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="in-kind">
                                            <Combobox.ItemText>In-Kind</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="paypal">
                                            <Combobox.ItemText>PayPal</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="property">
                                            <Combobox.ItemText>Property</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="stock">
                                            <Combobox.ItemText>Stock</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="terminal">
                                            <Combobox.ItemText>Terminal</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="venmo">
                                            <Combobox.ItemText>Venmo</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="other">
                                            <Combobox.ItemText>Other</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                        <Combobox.Item value="none">
                                            <Combobox.ItemText>None</Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                    </Combobox.Items>
                                </Combobox.Popover>
                            </Form.Combobox>
                            <Form.Error name={path('paymentMethod')} />
                        </Form.Container>

                        <p className="text-lg font-semibold">Map to QuickBooks as:</p>

                        <div className="flex flex-col gap-4">
                            <div className="flex justify-between gap-4">
                                <Form.Container>
                                    <Form.Label name={path('mappingServiceItem')}>
                                        Service item
                                    </Form.Label>

                                    <Form.Combobox
                                        resource="service item"
                                        control={control}
                                        name={path('mappingServiceItem')}
                                        allowMultiple={false}
                                        disabled={doNotSync}
                                    >
                                        <Combobox.Trigger aria-label="Select service item">
                                            <Combobox.Select placeholder="Select service item" />
                                        </Combobox.Trigger>
                                        <Combobox.Popover>
                                            <Combobox.Items>
                                                {mapping.data.item_service?.map(({ id, name }) => (
                                                    <Combobox.Item value={id} key={id}>
                                                        <Combobox.ItemText>
                                                            {name}
                                                        </Combobox.ItemText>
                                                        <Combobox.CheckmarkIndicator />
                                                    </Combobox.Item>
                                                ))}
                                                {mapping.data.item_service?.length === 0 && (
                                                    <Combobox.NoneFoundText>
                                                        No service items found
                                                    </Combobox.NoneFoundText>
                                                )}
                                            </Combobox.Items>
                                        </Combobox.Popover>
                                    </Form.Combobox>
                                    <Form.Error name={path('mappingServiceItem')} />
                                </Form.Container>

                                <Form.Container>
                                    <Form.Label name={path('mappingClass')}>Class</Form.Label>
                                    <Form.Combobox
                                        resource="class"
                                        control={control}
                                        name={path('mappingClass')}
                                        allowMultiple={false}
                                        disabled={doNotSync}
                                    >
                                        <Combobox.Trigger aria-label="Select class">
                                            <Combobox.Select placeholder="Select class" />
                                        </Combobox.Trigger>
                                        <Combobox.Popover>
                                            <Combobox.Items>
                                                {mapping.data.classes?.map(({ id, name }) => (
                                                    <Combobox.Item value={id} key={id}>
                                                        <Combobox.ItemText>
                                                            {name}
                                                        </Combobox.ItemText>
                                                        <Combobox.CheckmarkIndicator />
                                                    </Combobox.Item>
                                                ))}
                                                {mapping.data.classes?.length === 0 && (
                                                    <Combobox.NoneFoundText>
                                                        No classes found
                                                    </Combobox.NoneFoundText>
                                                )}
                                            </Combobox.Items>
                                        </Combobox.Popover>
                                    </Form.Combobox>
                                    <Form.Error name={path('mappingClass')} />
                                </Form.Container>
                            </div>

                            <Form.Container>
                                <Form.Label name={path('mappingAccount')}>
                                    Deposit account
                                </Form.Label>
                                <Form.Combobox
                                    resource="account"
                                    control={control}
                                    name={path('mappingAccount')}
                                    allowMultiple={false}
                                    disabled={doNotSync}
                                    rules={{
                                        validate: value =>
                                            doNotSync ||
                                            (Array.isArray(value) && value.length > 0) ||
                                            'Account is required when syncing',
                                    }}
                                >
                                    <Combobox.Trigger aria-label="Select account">
                                        <Combobox.Select placeholder="Select account" />
                                    </Combobox.Trigger>
                                    <Combobox.Popover>
                                        <Combobox.Items>
                                            {mapping.data.accounts?.map(({ id, name }) => (
                                                <Combobox.Item value={id} key={id}>
                                                    <Combobox.ItemText>{name}</Combobox.ItemText>
                                                    <Combobox.CheckmarkIndicator />
                                                </Combobox.Item>
                                            ))}
                                            {mapping.data.accounts?.length === 0 && (
                                                <Combobox.NoneFoundText>
                                                    No accounts found
                                                </Combobox.NoneFoundText>
                                            )}
                                        </Combobox.Items>
                                    </Combobox.Popover>
                                </Form.Combobox>
                                <Form.Error name={path('mappingAccount')} />
                            </Form.Container>
                        </div>

                        <Form.Container>
                            <Form.Label
                                className="flex items-center gap-3"
                                name={path('doNotSync')}
                            >
                                <Form.UseFormCheckboxInput
                                    control={control}
                                    name={path('doNotSync')}
                                />
                                Do not sync
                            </Form.Label>
                            <Form.Error name={path('doNotSync')} />
                        </Form.Container>

                        <p className="flex gap-1 text-xs">
                            Updated your QuickBooks service items, accounts, or classes recently?{' '}
                            <Button
                                variant="none"
                                onClick={() => refreshMapping()}
                                disabled={isRefreshingMapping}
                                className="text-blue-600"
                            >
                                Refresh details
                            </Button>
                        </p>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild>
                            <Button variant="outline" color="neutral">
                                Cancel
                            </Button>
                        </Drawer.Cancel>

                        <div className="flex items-center gap-4">
                            {!isEditing && (
                                <Form.SubmitButton
                                    variant="outline"
                                    onClick={() => setShouldExitAfterCreate(false)}
                                    isSubmitting={isPending}
                                >
                                    Create and add another
                                </Form.SubmitButton>
                            )}
                            <Form.SubmitButton
                                onClick={() => setShouldExitAfterCreate(true)}
                                disabled={isPending}
                            >
                                {isEditing ? 'Save' : 'Create'}
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.GivebutterFormProvider>
    );
};

const getInvalidRuleError = (reason?: InvalidReason) => {
    if (!reason) {
        return null;
    }

    const key = (
        {
            fund_not_found: 'fundId',
            campaign_not_found: 'campaignId',
            service_item_not_found: 'mappingServiceItem',
            class_not_found: 'mappingClass',
            account_not_found: 'mappingAccount',
        } as const
    )[reason];

    const message = {
        fund_not_found: 'Fund not found',
        campaign_not_found: 'Campaign not found',
        service_item_not_found: 'Service item not found',
        class_not_found: 'Class not found',
        account_not_found: 'Account not found',
    }[reason];

    return { key, message };
};

const transformRuleFormIntoRequest = (values: QuickBooksRuleFormValues): QuickBooksRuleRequest => {
    return {
        name: values.name,
        fund_id: values.fundId[0] ? parseInt(values.fundId[0]) : null,
        campaign_id: values.campaignId[0] ? parseInt(values.campaignId[0]) : null,
        payment_method: values.paymentMethod[0] ?? null,
        mapping: {
            service_item: values.mappingServiceItem?.[0] ?? null,
            class: values.mappingClass?.[0] ?? null,
            account: values.mappingAccount?.[0] ?? null,
        },
        do_not_sync: values.doNotSync,
    };
};

export { QuickBooksRuleDrawer };
